/**
 * The second eater's page (#1285).
 *
 * ⛔ NO SIGN-IN, and no app shell either. Somebody arriving here has been sent a
 * link by a housemate; they are not a user of this product and should not be
 * asked to become one. There is no nav, nothing to explore, and nothing here
 * about the household — just their name, sixteen cards, and one question about
 * lunch.
 *
 * This is the ticket that makes the other person a participant rather than
 * somebody who evaluates dinner after it is cooked. Everything else in the app
 * so far makes life easier for the person already doing all of it.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { twoPlates, AFFINITY_LABELS, LEFTOVER_LABELS, type FoodGroup, type TastePreset } from "../api/twoPlates";

const SCORES = [3, 2, 1, 0];
type Stage = "preset" | "cards" | "leftovers" | "done";

import { MemberKitchen } from "../components/MemberKitchen";

export default function LinkTaste() {
  const { token = "" } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["link", token],
    queryFn: () => twoPlates.link(token),
    retry: false,
  });

  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [stage, setStage] = useState<Stage>("preset");
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    if (!data) return;
    setAnswers(data.eater.answers);
    if (data.answeredAt) setStage("done");
    else if (Object.keys(data.eater.answers).length > 0) setStage("cards");
  }, [data]);

  const save = useMutation({
    mutationFn: (body: { affinities?: Record<string, number>; leftoverTolerance?: number; finished?: boolean }) =>
      twoPlates.saveLinkTaste(token, body),
  });

  if (isLoading) return <Shell><p className="text-muted text-sm">Loading…</p></Shell>;

  if (isError || !data) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold tracking-tight">This link is no longer good.</h1>
        <p className="text-muted mt-2 text-sm">Ask whoever sent it for a new one.</p>
      </Shell>
    );
  }

  const groups = data.groups;
  const answered = groups.filter((g) => answers[g.key] !== undefined).length;

  function applyPreset(preset: TastePreset | null) {
    if (!preset) { setStage("cards"); setCardIndex(0); return; }
    const next = { ...preset.answers, ...answers };
    setAnswers(next);
    save.mutate({ affinities: preset.answers });
    setStage("cards");
    setCardIndex(Math.max(0, groups.findIndex((g) => next[g.key] === undefined)));
  }

  function answer(group: FoodGroup, score: number) {
    const next = { ...answers, [group.key]: score };
    setAnswers(next);
    save.mutate({ affinities: { [group.key]: score } });
    const following = groups.findIndex((g, i) => i > cardIndex && next[g.key] === undefined);
    if (following !== -1) { setCardIndex(following); return; }
    const anyLeft = groups.findIndex((g) => next[g.key] === undefined);
    if (anyLeft === -1) setStage("leftovers");
    else setCardIndex(anyLeft);
  }

  if (stage === "done") {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold tracking-tight">Thanks, {data.eater.name}.</h1>
        <p className="text-muted mt-2 text-sm">
          That is on file. Dinner gets planned around what you said, not around what
          somebody guessed.
        </p>
        <button
          onClick={() => { setStage("cards"); setCardIndex(0); }}
          className="text-plate-b mt-6 text-sm underline underline-offset-4"
        >
          Change an answer
        </button>

        {/* ⛔ This used to be the end of the road. The person who has just
            answered sixteen questions is the one most willing to keep going, and
            whoever is actually cooking needs the recipe — so the link now opens
            the kitchen instead of thanking them and stopping (#1343). */}
        <MemberKitchen token={token!} />

        <p className="text-muted mt-8 text-xs">
          Keep this link. It is how you get back in — no account, no password.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="mb-6">
        <p className="text-muted text-xs uppercase tracking-wide">Two Plates</p>
        {stage === "cards" && (
          <>
            <div
              className="bg-line mt-3 h-1.5 overflow-hidden rounded-full"
              role="progressbar"
              aria-valuenow={answered}
              aria-valuemin={0}
              aria-valuemax={groups.length}
              aria-label="Foods answered"
            >
              <div
                className="bg-plate-b h-full rounded-full transition-[width] duration-300"
                style={{ width: `${(answered / groups.length) * 100}%` }}
              />
            </div>
            <p className="text-muted mt-2 text-xs">{answered} of {groups.length}</p>
          </>
        )}
      </header>

      {stage === "preset" && (
        <section>
          <h1 className="text-2xl font-semibold tracking-tight">
            Hi {data.eater.name} — what do you actually eat?
          </h1>
          <p className="text-muted mt-2 text-sm">
            Sixteen quick questions. Nobody sees anything about you except what goes on
            your plate.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            {data.presets.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                className="bg-surface border-line rounded-xl border p-4 text-left"
              >
                <p className="font-semibold">{p.name}</p>
                <p className="text-muted mt-1 text-sm">{p.note}</p>
              </button>
            ))}
            <button
              onClick={() => applyPreset(null)}
              className="text-muted mt-1 self-start text-sm underline underline-offset-4"
            >
              None of these — ask me all sixteen
            </button>
          </div>
        </section>
      )}

      {stage === "cards" && groups[cardIndex] && (
        <section>
          <div className="bg-surface border-line rounded-2xl border p-6">
            <p className="text-muted text-xs uppercase tracking-wide">How about</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">{groups[cardIndex].name}</h1>
            {groups[cardIndex].examples && (
              <p className="text-muted mt-2 text-sm">{groups[cardIndex].examples}</p>
            )}
          </div>
          <div className="mt-5 flex flex-col gap-2.5">
            {SCORES.map((score) => {
              const chosen = answers[groups[cardIndex].key] === score;
              return (
                <button
                  key={score}
                  onClick={() => answer(groups[cardIndex], score)}
                  aria-pressed={chosen}
                  className="tap border-line rounded-xl border px-5 text-left text-base font-medium"
                  style={chosen ? { borderColor: "var(--color-plate-b)", background: "color-mix(in srgb, var(--color-plate-b) 10%, transparent)" } : undefined}
                >
                  {AFFINITY_LABELS[score]}
                </button>
              );
            })}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setCardIndex(Math.max(0, cardIndex - 1))}
              disabled={cardIndex === 0}
              className="text-muted text-sm underline underline-offset-4 disabled:opacity-40"
            >
              Back
            </button>
            {answered === groups.length && (
              <button
                onClick={() => setStage("leftovers")}
                className="text-plate-b text-sm underline underline-offset-4"
              >
                One last question
              </button>
            )}
          </div>
        </section>
      )}

      {stage === "leftovers" && (
        <section>
          <h1 className="text-2xl font-semibold tracking-tight">
            Will you eat last night's dinner for lunch?
          </h1>
          <p className="text-muted mt-2 text-sm">
            Not a taste question. It decides how much gets cooked, not what.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            {[2, 1, 0].map((level) => (
              <button
                key={level}
                onClick={() =>
                  save.mutate(
                    { leftoverTolerance: level, finished: true },
                    { onSuccess: () => setStage("done") },
                  )
                }
                disabled={save.isPending}
                className="bg-surface border-line rounded-xl border p-4 text-left disabled:opacity-50"
              >
                <p className="font-semibold">{LEFTOVER_LABELS[level].title}</p>
                <p className="text-muted mt-1 text-sm">{LEFTOVER_LABELS[level].note}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {save.isError && (
        <p role="alert" className="text-warn mt-6 text-sm">
          That did not save. Check your connection and tap it again.
        </p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-md px-5 py-10">{children}</div>;
}
